export function LicensingFramework() {
  const steps = [
    {
      number: '1',
      title: 'Verify Eligibility',
      description: 'Confirm your property zoning allows short term rentals and check strata bylaws if applicable',
      timeline: '1-2 days',
      icon: 'ri-search-line'
    },
    {
      number: '2',
      title: 'Gather Documentation',
      description: 'Collect proof of ownership, property tax notice, floor plans, parking confirmation, and insurance',
      timeline: '3-5 days',
      icon: 'ri-file-list-3-line'
    },
    {
      number: '3',
      title: 'Submit Application',
      description: 'Complete business license application with your municipality (District or City)',
      timeline: '1 day',
      icon: 'ri-send-plane-line'
    },
    {
      number: '4',
      title: 'Inspection & Review',
      description: 'Municipality reviews application and may conduct property inspection for safety compliance',
      timeline: '4-8 weeks',
      icon: 'ri-shield-check-line'
    },
    {
      number: '5',
      title: 'Receive License',
      description: 'Once approved, receive your business license and display it prominently in your rental',
      timeline: '1-2 weeks',
      icon: 'ri-award-line'
    }
  ];

  const requiredDocs = [
    'Proof of property ownership (title or tax notice)',
    'Government-issued photo ID',
    'Property floor plan showing rental area',
    'Proof of adequate parking spaces',
    'Commercial general liability insurance ($2M minimum)',
    'Strata approval letter (if applicable)',
    'Fire safety plan and smoke detector certification',
    'Septic approval (for properties not on municipal sewer)'
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Licensing Framework
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Both District and City of North Vancouver require business licenses for short term rentals. Here's the complete process.
          </p>
        </div>

        <div className="mb-20">
          <h3 className="text-2xl font-bold text-[#505A5A] mb-10 text-center">
            5-Step Licensing Process
          </h3>
          <div className="relative">
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-1 bg-[#F0F0F0]"></div>
            
            <div className="grid md:grid-cols-5 gap-8 relative">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-32 h-32 flex items-center justify-center bg-[#882629] rounded-full mb-6 relative z-10 shadow-lg">
                      <i className={`${step.icon} text-4xl text-white`}></i>
                    </div>
                    <div className="bg-[#F0F0F0] rounded-2xl p-6 h-full">
                      <div className="text-3xl font-bold text-[#882629] mb-3">
                        {step.number}
                      </div>
                      <h4 className="text-lg font-bold text-[#505A5A] mb-3">
                        {step.title}
                      </h4>
                      <p className="text-sm text-[#505A5A]/70 mb-4 leading-relaxed">
                        {step.description}
                      </p>
                      <div className="inline-block bg-white px-4 py-2 rounded-full text-sm font-medium text-[#882629]">
                        {step.timeline}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="bg-[#F0F0F0] rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-[#505A5A] mb-6">
              Required Documentation
            </h3>
            <ul className="space-y-4">
              {requiredDocs.map((doc, index) => (
                <li key={index} className="flex items-start gap-3">
                  <i className="ri-file-text-line text-[#882629] text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-[#505A5A]/80 leading-relaxed">
                    {doc}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-6">
            <div className="bg-white border-2 border-[#882629] rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#882629] rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-[#505A5A] mb-2">
                    Total Timeline
                  </h4>
                  <p className="text-3xl font-bold text-[#882629]">
                    6-12 weeks
                  </p>
                </div>
              </div>
              <p className="text-[#505A5A]/70 leading-relaxed">
                From initial application to receiving your license. Processing times vary by municipality and season.
              </p>
            </div>

            <div className="bg-white border-2 border-[#882629] rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#882629] rounded-lg flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-[#505A5A] mb-2">
                    License Fees
                  </h4>
                  <p className="text-3xl font-bold text-[#882629]">
                    $200-$500
                  </p>
                </div>
              </div>
              <p className="text-[#505A5A]/70 leading-relaxed">
                Annual business license fee varies between District and City. Renewal required each year.
              </p>
            </div>

            <div className="bg-[#882629]/5 border-l-4 border-[#882629] rounded-lg p-6">
              <div className="flex items-start gap-3">
                <i className="ri-lightbulb-line text-xl text-[#882629] flex-shrink-0 mt-1"></i>
                <div>
                  <h4 className="font-semibold text-[#505A5A] mb-2">
                    Pro Tip
                  </h4>
                  <p className="text-sm text-[#505A5A]/70 leading-relaxed">
                    Start your application early and ensure all documentation is complete. Incomplete applications cause the majority of delays in the licensing process.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
